package one.healthium.profile.ui.update_height

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.GENERAL_SAVE
import loco.UPDATE_HEIGHT_HIGH_VALUE_ERROR_FORMAT
import loco.UPDATE_HEIGHT_LOW_VALUE_ERROR_FORMAT
import loco.UPDATE_HEIGHT_SCREEN_TITLE
import one.healthium.base.utils.cmToFt
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.components.CmInput
import one.healthium.ui.components.FtInput
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

interface UpdateHeightEventDelegate {
    fun onCloseClicked()
    fun onHeightUpdated()
}

@Composable
fun UpdateHeightScreen(eventDelegate: UpdateHeightEventDelegate) {
    val viewModel = koinViewModel<UpdateHeightViewModel>()
    val state by viewModel.state.collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            UpdateHeightEvent.OnHeightUpdated -> eventDelegate.onHeightUpdated()
        }
    }

    UpdateHeightScreenContent(
        state = state,
        onHeightUpdated = viewModel::onHeightUpdated,
        onCloseClicked = eventDelegate::onCloseClicked,
        onSaveClicked = viewModel::onSaveClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun UpdateHeightScreenContent(
    state: UpdateHeightUiState,
    onHeightUpdated: (Float) -> Unit,
    onCloseClicked: () -> Unit,
    onSaveClicked: () -> Unit,
) {
    val validationError = state.height?.getError(isMetric = state.isMetric)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppSheetTopBar(
            onCloseClicked = onCloseClicked,
        )
        Spacer(Modifier.height(14.dp))
        Text(
            modifier = Modifier.padding(horizontal = 32.dp),
            text = stringResource(loco.Res.string.UPDATE_HEIGHT_SCREEN_TITLE),
            style = AppTheme.logControlStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(48.dp))

        when (state.isMetric) {
            true -> {
                CmInput(
                    modifier = Modifier.padding(horizontal = 25.dp),
                    value = state.height,
                    onValueChanged = { onHeightUpdated(it ?: 0f) },
                    showKeyboardAutomatically = true,
                    showKeyboardDelay = 450.milliseconds,
                )
            }

            false -> {
                FtInput(
                    modifier = Modifier.padding(horizontal = 25.dp),
                    value = state.height,
                    isValid = { validationError == null },
                    onValueChanged = { onHeightUpdated(it ?: 0f) },
                    unitColor = AppTheme.colors.primary,
                    showKeyboardAutomatically = true,
                    showKeyboardDelay = 450.milliseconds,
                )
            }
        }
        Spacer(Modifier.height(36.dp))
        Text(
            text = validationError ?: "",
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(modifier = Modifier.height(60.dp))
        AppProgressButton(
            modifier = Modifier.defaultMinSize(minWidth = 176.dp),
            text = stringResource(loco.Res.string.GENERAL_SAVE),
            loading = state.loading,
            onClick = onSaveClicked,
        )
        Spacer(Modifier.height(50.dp))
    }
}

@Composable
private fun Float?.getError(isMetric: Boolean): String? {
    if (this == null || this == 0f) return null

    return when (isMetric) {
        true -> {
            if (this < 40) {
                stringResource(loco.Res.string.UPDATE_HEIGHT_LOW_VALUE_ERROR_FORMAT, 40)
            } else if (this > 240) {
                stringResource(loco.Res.string.UPDATE_HEIGHT_HIGH_VALUE_ERROR_FORMAT, 240)
            } else null
        }

        else -> {
            val valueInFt = this.cmToFt()
            if (valueInFt < 3) {
                stringResource(loco.Res.string.UPDATE_HEIGHT_LOW_VALUE_ERROR_FORMAT, 3)
            } else if (valueInFt > 14) {
                stringResource(loco.Res.string.UPDATE_HEIGHT_HIGH_VALUE_ERROR_FORMAT, 14)
            } else null
        }
    }
}
